import { createApiClient } from '@alascorporation/api-client'
import { identifyPostHogUser, resetPostHog } from '@alascorporation/observability'
import { posthog } from '../observability/posthog'
import { env } from '../config/env'

export const TOKEN_KEY = 'token'
export const USER_KEY = 'user'

export const api = createApiClient({
  baseURL: env.apiUrl,
  getToken: () => localStorage.getItem(TOKEN_KEY),
  onUnauthorized: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },
})

export interface HubUser {
  id: string
  email: string
  name: string
  role: 'student' | 'instructor' | 'admin'
  avatarUrl?: string | null
  bio?: string | null
  phone?: string | null
  country?: string | null
}

export interface ProfileUpdate {
  name?: string
  avatarUrl?: string
  bio?: string
  phone?: string
  country?: string
}

export interface StudentProfile {
  firstName?: string | null
  lastName?: string | null
  documentType?: string | null
  documentNumber?: string | null
  birthDate?: string | null
  gender?: 'M' | 'F' | 'NB' | 'ND' | null
  countryCode?: string | null
  residenceCountryCode?: string | null
  stateProvince?: string | null
  city?: string | null
  address?: string | null
  guardianName?: string | null
  guardianRelationship?: string | null
  guardianDocumentType?: string | null
  guardianDocumentNumber?: string | null
  guardianPhone?: string | null
  guardianEmail?: string | null
  bloodType?: string | null
  hasDisability?: boolean | null
  disabilityDetail?: string | null
  medications?: string | null
  institution?: string | null
  selfDeclaredRole?: string | null
  academicLevel?: string | null
}

export interface EcosystemApp {
  id: string
  name: string
  description: string
  url: string
}

export interface AccountEntitlement {
  id: string
  userId: string | null
  teamId: string | null
  product: string
  scope: string
  origin: string
  status: string
  startsAt: string
  endsAt: string | null
}

export interface Team {
  id: string
  name: string
  createdById: string
  createdAt: string
  myRole?: 'owner' | 'member'
}

export interface TeamMember {
  id: string
  userId: string
  role: 'owner' | 'member'
  user: { id: string; name: string; email: string }
}

export interface TeamDetail extends Team {
  members: TeamMember[]
}

export interface Certificate {
  id: string
  certificateCode: string
  issuedAt: string
  course_title: string
  category: string
}

export async function getEcosystemApps(): Promise<EcosystemApp[]> {
  const { data } = await api.get('/auth/apps')
  return data.data as EcosystemApp[]
}

export async function getMyEntitlements(): Promise<AccountEntitlement[]> {
  const { data } = await api.get('/auth/me/entitlements')
  return data.data as AccountEntitlement[]
}

export async function getMyTeams(): Promise<Team[]> {
  const { data } = await api.get('/teams/mine')
  return data.data as Team[]
}

export async function getTeam(teamId: string): Promise<TeamDetail> {
  const { data } = await api.get(`/teams/${teamId}`)
  return data.data as TeamDetail
}

export async function createTeam(name: string): Promise<Team> {
  const { data } = await api.post('/teams', { name })
  return data.data as Team
}

export async function inviteToTeam(teamId: string, email: string): Promise<void> {
  await api.post(`/teams/${teamId}/invites`, { email })
}

export async function getMyCertificates(): Promise<Certificate[]> {
  const { data } = await api.get('/academy/certificates/my')
  return data.data as Certificate[]
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.put('/auth/password', { currentPassword, newPassword })
}

export async function updateMyProfile(fields: ProfileUpdate): Promise<HubUser> {
  const { data } = await api.put('/auth/profile', fields)
  return data.data as HubUser
}

export async function getMyStudentProfile(): Promise<StudentProfile | null> {
  const { data } = await api.get('/auth/me')
  return (data.data.profile as StudentProfile | null) ?? null
}

export async function updateMyStudentProfile(fields: StudentProfile): Promise<StudentProfile> {
  const { data } = await api.put('/auth/profile', { profile: fields })
  return data.data.profile as StudentProfile
}

export function getCertificateVerifyUrl(certificateCode: string): string {
  return `${env.apiUrl}/academy/certificates/verify/${certificateCode}`
}

export function getStoredUser(): HubUser | null {
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as HubUser
  } catch {
    return null
  }
}

export function storeSession(user: HubUser, token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(USER_KEY, JSON.stringify(user))
  identifyPostHogUser(posthog, { id: user.id, email: user.email, role: user.role })
}

export function mergeStoredUser(patch: Partial<HubUser>): void {
  const current = getStoredUser()
  if (!current) return
  localStorage.setItem(USER_KEY, JSON.stringify({ ...current, ...patch }))
}

export function hasValidSession(): boolean {
  return !!localStorage.getItem(TOKEN_KEY)
}

/** Clears the local session and revokes the shared "sl_session" cookie
 * server-side, so a logout here also ends the session on every other
 * *.satelab.org app instead of just this tab. */
export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  resetPostHog(posthog)
  api.post('/auth/logout').catch(() => {
    // Best-effort — the local session is already cleared either way.
  })
  window.location.reload()
}
